import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import LeadDetail from "@/pages/LeadDetail";
import { useAuth } from "@/context/AuthContext";
import { STATUS_COLORS, PRIORITY_COLORS } from "@/lib/constants";
import { CalendarClock, Phone, Mail, Mail as MailIcon, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function FollowUps() {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const [leads, setLeads] = useState([]);
  const [members, setMembers] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/followups").then((r) => setLeads(r.data));
    if (isAdmin) api.get("/users").then((r) => setMembers(r.data));
  }, [isAdmin]);

  const today = new Date().toISOString().slice(0, 10);
  const overdue = leads.filter((l) => l.follow_up_date && l.follow_up_date < today);
  const todayList = leads.filter((l) => l.follow_up_date === today);
  const upcoming = leads
    .filter((l) => l.follow_up_date && l.follow_up_date > today)
    .sort((a, b) => a.follow_up_date.localeCompare(b.follow_up_date));

  const Section = ({ title, items, tone = "default", testid }) => (
    <div className="solid-panel p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold font-semibold">{title}</h2>
        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${tone === "danger" ? "bg-rose-50 text-rose-700 border-rose-200" : tone === "warn" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 italic">Nothing here — you're all caught up.</p>
      ) : (
        <div className="space-y-2">
          {items.map((l) => (
            <div key={l.id} onClick={() => setDetail(l)} className="glass rounded-xl p-4 cursor-pointer glass-hover flex items-start sm:items-center justify-between gap-3 flex-wrap sm:flex-nowrap" data-testid={`followup-card-${l.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium truncate max-w-full">{l.name}</p>
                  <span className={STATUS_COLORS[l.status]}>{l.status}</span>
                  <span className={PRIORITY_COLORS[l.priority]}>{l.priority}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                  {l.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone}</span>}
                  {l.email && <span className="flex items-center gap-1 truncate max-w-full"><Mail className="w-3 h-3 flex-shrink-0" /><span className="truncate">{l.email}</span></span>}
                  {l.company && <span className="truncate">{l.company}</span>}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="flex items-center gap-1 text-sm">
                  <CalendarClock className="w-4 h-4 text-[#1D63ED]" />
                  <span>{l.follow_up_date}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="followups-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">Follow-ups</p>
          <h1 className="text-xl font-semibold mt-1 text-slate-900">Your Schedule</h1>
          <p className="text-slate-500 mt-1 text-sm">Never miss a call. Never lose a lead.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-slate-200 bg-white hover:bg-slate-50 h-9"
            data-testid="email-mine-btn"
            onClick={async () => {
              try {
                const { data } = await api.post("/reminders/send-mine");
                if (data.sent) toast.success(`Reminder emailed with ${(data.counts.overdue||0)+(data.counts.today||0)+(data.counts.abandoned||0)} lead(s)`);
                else if (data.reason === "throttled") toast.info(`Already sent recently — try again in ${data.retry_after_minutes} min`);
                else if (data.reason === "no items") toast.info("Nothing to email — you're all clear!");
                else toast.error("Email could not be sent");
              } catch (e) { toast.error(formatApiError(e)); }
            }}
          >
            <MailIcon className="w-4 h-4 mr-2" /> Email me my reminders
          </Button>
          {isAdmin && (
            <Button
              className="h-9 bg-[#1D63ED] hover:bg-[#1854D9] text-white"
              data-testid="email-all-btn"
              onClick={async () => {
                try {
                  const { data } = await api.post("/reminders/send-all");
                  toast.success(`Sent to ${data.sent} of ${data.attempted} member(s)`);
                } catch (e) { toast.error(formatApiError(e)); }
              }}
            >
              <Send className="w-4 h-4 mr-2" /> Email whole team
            </Button>
          )}
        </div>
      </header>

      <Section title="Overdue" items={overdue} tone="danger" testid="section-overdue" />
      <Section title="Due Today" items={todayList} tone="warn" testid="section-today" />
      <Section title="Upcoming" items={upcoming} testid="section-upcoming" />

      <LeadDetail lead={detail} open={!!detail} onOpenChange={(v) => !v && setDetail(null)} members={members} onChanged={() => api.get("/followups").then((r) => setLeads(r.data))} />
    </div>
  );
}
